/**
 * 电梯运动学（梯形速度曲线，简化模型）—— 设计文档 3.2。
 *
 * 内核调度 CAR_ARRIVED 时刻与渲染层插值共用同一实现，
 * 保证 AC-F015-01（画面与日志一致）与 AC-F012-02（物理一致性，偏差 < 1%）。
 */

export interface KinematicsParams {
  /** 相邻楼层间距（米）。 */
  readonly floorHeight: number;
  /** 额定速度（m/s）。 */
  readonly ratedSpeed: number;
  /** 加速度（m/s²）。 */
  readonly acceleration: number;
}

/** 走过 `floors` 个楼层间距的距离（米，非负）。 */
export function distanceForFloors(floors: number, p: KinematicsParams): number {
  return Math.abs(floors) * p.floorHeight;
}

/**
 * 走过距离 d（米）的运行时间（毫秒，整数）。
 *
 * - 若能加速到额定速度（2·s_a ≤ d）：梯形曲线 t = d/v + v/a
 * - 否则（三角形曲线，达不到额定速度）：t = 2·√(d/a)
 *
 * 时间统一取整毫秒（D6），取整误差 < 1% 远在 AC-F012-02 容差内。
 */
export function travelTimeMs(d: number, p: KinematicsParams): number {
  const { ratedSpeed: v, acceleration: a } = p;
  if (d <= 0) return 0;
  const s_a = (v * v) / (2 * a); // 加速到额定速度所需距离
  let tSec: number;
  if (2 * s_a <= d) {
    tSec = d / v + v / a; // 加速 + 匀速 + 减速
  } else {
    tSec = 2 * Math.sqrt(d / a); // 加速到中点即减速
  }
  return Math.round(tSec * 1000);
}

/** 走过 `floors` 个楼层间距的运行时间（毫秒）。 */
export function travelTimeForFloorsMs(floors: number, p: KinematicsParams): number {
  return travelTimeMs(distanceForFloors(floors, p), p);
}

/**
 * 在距离 d 的运行段内，经过 elapsedSec 秒后的位置（米，距起点）。
 * 用于渲染层在两个快照间做运动学插值（D4），返回值被 clamp 到 [0, d]。
 */
export function positionAt(d: number, elapsedSec: number, p: KinematicsParams): number {
  const { ratedSpeed: v, acceleration: a } = p;
  if (d <= 0 || elapsedSec <= 0) return 0;

  const s_a = (v * v) / (2 * a);

  if (2 * s_a <= d) {
    // 梯形曲线
    const tAcc = v / a;
    const tCruise = (d - 2 * s_a) / v;
    const total = tAcc + tCruise + tAcc;
    if (elapsedSec >= total) return d;
    if (elapsedSec <= tAcc) return 0.5 * a * elapsedSec * elapsedSec; // 加速段
    if (elapsedSec <= tAcc + tCruise) return s_a + v * (elapsedSec - tAcc); // 匀速段
    const tau = elapsedSec - tAcc - tCruise; // 减速段内时间
    return d - s_a + v * tau - 0.5 * a * tau * tau;
  }

  // 三角形曲线
  const tPeak = Math.sqrt(d / a);
  const total = 2 * tPeak;
  if (elapsedSec >= total) return d;
  if (elapsedSec <= tPeak) return 0.5 * a * elapsedSec * elapsedSec; // 加速段
  const tau = elapsedSec - tPeak; // 减速段内时间
  const peakPos = 0.5 * a * tPeak * tPeak; // = d/2
  const vPeak = a * tPeak;
  return peakPos + vPeak * tau - 0.5 * a * tau * tau;
}
